// P2-25 command dispatch handlers (design §5.4, §6.2). Wires the
// framework-agnostic `CommandPoller` (`./command-poll.ts`) to the real Pinia
// stores / router / query cache. Kept in its own module (rather than inline
// in `../stores/auth.ts`) so it's unit-testable without booting a whole auth
// store test and so `auth.ts` stays focused on session state.
//
// `createCommandHandlers()` reads the Pinia stores at CALL time (not module
// load time) via the plain `useXStore()` accessors with no arguments — this
// works anywhere once `app.use(pinia)` has run (same pattern the router
// guard in `../router/index.ts` already uses), and keeps this module free of
// a Pinia-instance parameter that every caller would otherwise have to thread
// through.
import type {
  CommandDispatchHandlers,
  DataChangedPayload,
  ForceLogoutPayload,
} from './command-poll';
import { clearAccessToken } from './session';
import { queryClient } from './query-client';
import { useAuthStore } from '@/stores/auth';
import { useConfigStore } from '@/stores/config';
import { useCommandsStore } from '@/stores/commands';
import { useToast } from '@/composables/useToast';
import { router } from '@/router';

/** Text-only copy for each §4 `forceLogoutReasonSchema` value (never
 * `v-html` — §10.6 — the toast composable renders these as plain strings). A
 * server-supplied `payload.message` (if present) overrides the default body,
 * still rendered as text only. */
const FORCE_LOGOUT_COPY: Record<ForceLogoutPayload['reason'], { title: string; message: string }> =
  {
    SESSION_SUPERSEDED: {
      title: 'Signed out',
      message: 'This account was signed in on another device.',
    },
    ADMIN_REVOKED: {
      title: 'Signed out',
      message: 'An administrator signed you out.',
    },
    ACCOUNT_SUSPENDED: {
      title: 'Account suspended',
      message: 'Your account access has been suspended.',
    },
    PASSWORD_CHANGED: {
      title: 'Signed out',
      message: 'Your password was changed. Please sign in again.',
    },
  };

/** Query-key prefixes owned by each `DATA_CHANGED` entity (design §5.4: "Map
 * `entity` to the owning Pinia store / query key and invalidate"). The app's
 * query keys are namespaced by top-level surface (`me`, `teacher`, `admin`,
 * `plans`, `catalogue` — see `../data/*`), not by domain entity, so a given
 * entity is mapped to every surface that can render it. `id`/`op` are not
 * used for finer-grained targeting: none of today's surfaces key their
 * queries by this entity's raw id, so a collection-level invalidation of
 * every owning prefix is the correct (if slightly broader-than-minimal)
 * behaviour for both `id === null` and a single-id change. TanStack Query's
 * `invalidateQueries({ queryKey: prefix })` matches every cached key that
 * starts with `prefix`. */
const DATA_CHANGED_QUERY_PREFIXES: Record<DataChangedPayload['entity'], string[][]> = {
  EXAM_BOARD: [['teacher'], ['me'], ['catalogue']],
  LEVEL: [['teacher'], ['me'], ['catalogue']],
  SUBJECT: [['teacher'], ['me'], ['catalogue']],
  SECTION: [['teacher'], ['me'], ['catalogue']],
  RESOURCE_TAB: [['teacher'], ['me'], ['catalogue']],
  RESOURCE: [['teacher'], ['me'], ['catalogue']],
  ASSET: [['teacher'], ['me'], ['catalogue']],
  ENROLLMENT: [['me'], ['admin']],
  SUBSCRIPTION: [['me'], ['admin'], ['plans']],
  USER: [['admin']],
  NOTIFICATION: [['me']],
};

/** Formats a `SESSION_SUPERSEDED` `supersededAt` ISO-8601 timestamp (session-superseded-UX
 * task) into a short local-time string for the teardown toast, e.g. `3:42 PM`. Returns `null`
 * for anything that isn't a parseable timestamp — absent (older cached client / pre-field
 * server), malformed, or otherwise invalid — so the caller can fall back to the generic copy
 * instead of ever rendering "undefined"/"Invalid Date". Deliberately time-only, no date/IP/
 * device info: the payload never carries anything else about the other session anyway. */
function formatSupersededAt(iso: string | undefined): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  try {
    return new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(
      date,
    );
  } catch {
    return null;
  }
}

/** Shared teardown for both a real `FORCE_LOGOUT` command and the §6.2
 * `401 SESSION_SUPERSEDED` poll mapping — the poller's `dispatchForceLogout`
 * is the SAME single call site for both (`../lib/command-poll.ts`), so there
 * is exactly one handler here too. Ack of a real command already happened
 * (poller acks before teardown, §3.3); this only ever clears LOCAL state and
 * navigates (I-3 — never a privileged API call, never awaited before the
 * redirect). */
function teardownSession(title: string, message: string, reason: string): void {
  const authStore = useAuthStore();
  const toast = useToast();

  // I-3: this handler must only ever clear LOCAL state and navigate — never
  // call a privileged API. In particular it must NOT call `authApi.logout()`:
  // that call authenticates via the browser's shared refresh cookie
  // (`credentials: 'include'`), not this tab's own in-memory access token. On
  // a SESSION_SUPERSEDED teardown the tab being evicted may share a browser
  // with the new legitimate tab, and by that point the shared cookie belongs
  // to the NEW session — calling the server logout here would revoke the new
  // session's refresh token and kill it. (The USER-INITIATED logout action in
  // `stores/auth.ts` is a separate, correct code path that does call the
  // server.)
  clearAccessToken();
  authStore.resetSession();
  toast.error(title, message);
  void router.replace({ name: 'auth-login', query: { reason } });
}

/** Builds the four §5.4 dispatch handlers + the §5.2 `onAuthExpired` hook.
 * Called once per `commandPoller.start(...)` (from `authStore.applySession`
 * and `boot/index.ts`) — cheap, no state of its own beyond closures over the
 * (call-time-resolved) stores. */
export function createCommandHandlers(): CommandDispatchHandlers {
  return {
    onForceLogout(payload) {
      const copy = FORCE_LOGOUT_COPY[payload.reason];
      // session-superseded-UX task: prefer a human-readable message built from
      // `supersededAt` over the generic "another device" copy when we have it — a server
      // `payload.message` (real FORCE_LOGOUT command only; never sent for the §6.2 poll-401
      // mapping) still wins over both. Falls back to the existing generic copy whenever
      // `supersededAt` is absent/unparseable so this never regresses older clients or shows
      // "undefined"/"Invalid Date".
      const supersededTime =
        payload.reason === 'SESSION_SUPERSEDED' ? formatSupersededAt(payload.supersededAt) : null;
      const message =
        payload.message ??
        (supersededTime
          ? `You were signed out because your account was signed in elsewhere at ${supersededTime}.`
          : copy.message);
      teardownSession(copy.title, message, payload.reason);
    },

    onJobStatus(payload) {
      useCommandsStore().upsertJobStatus(payload);
    },

    async onReload(payload) {
      switch (payload.scope) {
        case 'APP':
          await queryClient.invalidateQueries();
          break;
        case 'CONFIG':
          await useConfigStore().load();
          break;
        case 'ENTITLEMENTS':
          await useAuthStore().fetchMe();
          break;
        default:
          // Exhaustive per the current v1 `reloadPayloadSchema` enum — a
          // future scope is already filtered out by the poller's per-element
          // zod parse before reaching here.
          console.warn('[command-handlers] unknown RELOAD scope, ignoring', payload);
      }
    },

    async onDataChanged(payload) {
      const prefixes = DATA_CHANGED_QUERY_PREFIXES[payload.entity];
      if (!prefixes) {
        // Forward-compat (§4): a future entity the client doesn't know yet.
        console.warn('[command-handlers] unknown DATA_CHANGED entity, ignoring', payload);
        return;
      }
      await Promise.all(prefixes.map((queryKey) => queryClient.invalidateQueries({ queryKey })));
    },

    onAuthExpired() {
      // §5.2 / command-poll.ts: fires only after the poller's own single
      // silent refresh attempt already failed (or no `refresh` dep was
      // supplied) — "treat it like a FORCE_LOGOUT teardown". Not a real
      // FORCE_LOGOUT reason, so it gets its own copy/query value rather than
      // reusing a `forceLogoutReasonSchema` literal.
      teardownSession('Session expired', 'Please sign in again.', 'AUTH_EXPIRED');
    },
  };
}
